import React, { useState, useEffect } from 'react';
import { 
  Send, 
  Users, 
  Settings, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Building2,
  GitBranch,
  Plus,
  Save,
  X,
  Edit,
  Trash2,
  Clock,
  User
} from 'lucide-react';
import { WorkflowService } from '../services/WorkflowService';
import { TenantService } from '../services/TenantService';

const WorkflowAssignment = ({ userProfile }) => {
  const [templates, setTemplates] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  
  // Assignment configuration
  const [assignmentConfig, setAssignmentConfig] = useState({
    approvers: [],
    executors: [],
    notificationEmails: [],
    timeoutHours: 48,
    escalationEnabled: true,
    customFields: []
  });

  // For adding approvers
  const [newApprover, setNewApprover] = useState({
    stage: '',
    type: 'specific', // 'specific', 'role', 'group'
    value: '',
    name: '',
    email: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesData, tenantsData] = await Promise.all([
        WorkflowService.getWorkflowTemplates(),
        TenantService.getAllTenants()
      ]);
      
      setTemplates(templatesData);
      setTenants(tenantsData);
      
      // Load existing assignments
      const allAssignments = [];
      for (const tenant of tenantsData) {
        const tenantWorkflows = await WorkflowService.getTenantWorkflows(tenant.id);
        allAssignments.push(...tenantWorkflows.map(w => ({
          ...w,
          tenantName: tenant.name
        })));
      }
      setAssignments(allAssignments);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorkflow = () => {
    if (!selectedTemplate || !selectedTenant) {
      alert('Please select both a template and a tenant');
      return;
    }
    
    // Fix stage IDs if they're using old format (timestamps)
    const fixedTemplate = {
      ...selectedTemplate,
      stages: selectedTemplate.stages.map((stage, index) => {
        // Check if stage ID is in old format (contains timestamp)
        if (stage.id && !stage.id.match(/^stage_\d{1,2}$/)) {
          return {
            ...stage,
            id: `stage_${index + 1}` // Convert to proper format
          };
        }
        return stage;
      })
    };
    
    // Initialize config with template stages
    // Include all stages except 'notify' type which doesn't need approvers
    const initialApprovers = fixedTemplate.stages
      .filter(stage => stage.type !== 'notify')
      .map(stage => ({
        stageId: stage.id,
        stageName: stage.name || `Stage ${stage.id.replace('stage_', '')}`,
        approvers: []
      }));
    
    setAssignmentConfig(prev => ({
      ...prev,
      approvers: initialApprovers
    }));
    
    setEditingAssignment(null);
    setShowAssignModal(true);
  };
  
  const handleEditAssignment = (assignment) => {
    const template = assignment.template || templates.find(t => t.id === assignment.templateId);
    
    // Fix stage IDs if they're using old format (timestamps)
    const fixedTemplate = template ? {
      ...template,
      stages: (template.stages || []).map((stage, index) => {
        // Check if stage ID is in old format (contains timestamp)
        if (stage.id && !stage.id.match(/^stage_\d{1,2}$/)) {
          return {
            ...stage,
            id: `stage_${index + 1}` // Convert to proper format
          };
        }
        return stage;
      })
    } : template;
    
    setSelectedTemplate(fixedTemplate);
    setSelectedTenant(tenants.find(t => t.id === assignment.tenantId));
    
    // Ensure all config properties are properly initialized
    const config = assignment.config || {};
    
    // Fix stage IDs in existing approvers config if needed
    const fixedApprovers = config.approvers ? config.approvers.map((approverStage, index) => {
      if (approverStage.stageId && !approverStage.stageId.match(/^stage_\d{1,2}$/)) {
        return {
          ...approverStage,
          stageId: `stage_${index + 1}` // Convert to proper format
        };
      }
      return approverStage;
    }) : [];
    
    setAssignmentConfig({
      approvers: fixedApprovers,
      executors: config.executors || [],
      notificationEmails: config.notificationEmails || [],
      timeoutHours: config.timeoutHours || 48,
      escalationEnabled: config.escalationEnabled !== undefined ? config.escalationEnabled : true,
      customFields: config.customFields || []
    });
    
    setEditingAssignment(assignment);
    setShowAssignModal(true);
  };

  const addApprover = () => {
    if (!newApprover.stage || !newApprover.email) {
      alert('Please select a stage and enter an email');
      return;
    }
    
    setAssignmentConfig(prev => ({
      ...prev,
      approvers: prev.approvers.map(stage => 
        stage.stageId === newApprover.stage
          ? {
              ...stage,
              approvers: [...stage.approvers, {
                type: newApprover.type,
                email: newApprover.email,
                name: newApprover.name || newApprover.email,
                id: `approver_${Date.now()}`
              }]
            }
          : stage
      )
    }));
    
    // Reset form
    setNewApprover({
      stage: '',
      type: 'specific',
      value: '',
      name: '',
      email: ''
    });
  };

  const removeApprover = (stageId, approverId) => {
    setAssignmentConfig(prev => ({
      ...prev,
      approvers: prev.approvers.map(stage => 
        stage.stageId === stageId
          ? {
              ...stage,
              approvers: stage.approvers.filter(a => a.id !== approverId)
            }
          : stage
      )
    }));
  };

  const saveAssignment = async () => {
    try {
      setLoading(true);
      
      let result;
      if (editingAssignment) {
        // Update existing assignment
        result = await WorkflowService.updateWorkflowAssignment(
          editingAssignment.id,
          {
            config: assignmentConfig,
            updatedBy: userProfile.uid
          }
        );
        console.log('✅ Workflow assignment updated');
      } else {
        // Create new assignment
        result = await WorkflowService.assignWorkflowToTenant(
          selectedTemplate.id,
          selectedTenant.id,
          {
            ...assignmentConfig,
            assignedBy: userProfile.uid,
            templateName: selectedTemplate.name,
            tenantName: selectedTenant.name
          }
        );
        console.log('✅ Workflow assigned successfully');
      }
      
      if (result.success) {
        setShowAssignModal(false);
        setSelectedTemplate(null);
        setSelectedTenant(null);
        setEditingAssignment(null);
        setAssignmentConfig({
          approvers: [],
          executors: [],
          notificationEmails: [],
          timeoutHours: 48,
          escalationEnabled: true,
          customFields: []
        });
        
        // Reload assignments
        await loadData();
      }
    } catch (error) {
      console.error('Error assigning workflow:', error);
      alert('Failed to assign workflow');
    } finally {
      setLoading(false);
    }
  };

  const removeAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to remove this workflow assignment?')) {
      return;
    }
    
    try {
      // For now, we'll just mark it as inactive
      // In production, you'd want to implement proper deletion
      console.log('Removing assignment:', assignmentId);
      await loadData();
    } catch (error) {
      console.error('Error removing assignment:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Send className="h-6 w-6 text-purple-600" />
            Workflow Assignments
          </h2>
          <p className="text-gray-600 mt-1">
            Deploy workflow templates to your tenants
          </p>
        </div>
      </div>

      {/* Assignment Creator */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">Assign Workflow to Tenant</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Template
            </label>
            <select
              value={selectedTemplate?.id || ''}
              onChange={(e) => setSelectedTemplate(templates.find(t => t.id === e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Choose template...</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {/* Tenant Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Tenant
            </label>
            <select
              value={selectedTenant?.id || ''}
              onChange={(e) => setSelectedTenant(tenants.find(t => t.id === e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Choose tenant...</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>

          {/* Assign Button */}
          <div className="flex items-end">
            <button
              onClick={handleAssignWorkflow}
              disabled={!selectedTemplate || !selectedTenant}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Settings size={16} />
              Configure & Assign
            </button>
          </div>
        </div>

        {/* Selected Info */}
        {(selectedTemplate || selectedTenant) && (
          <div className="flex gap-4 p-3 bg-purple-50 rounded-lg">
            {selectedTemplate && (
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-purple-600" />
                <span className="text-sm">
                  <strong>Template:</strong> {selectedTemplate.name}
                </span>
              </div>
            )}
            {selectedTenant && (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-purple-600" />
                <span className="text-sm">
                  <strong>Tenant:</strong> {selectedTenant.name}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Current Assignments */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Current Assignments</h3>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8">
              <Send className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900">No assignments yet</h3>
              <p className="text-gray-600 mt-1">Assign workflow templates to tenants to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {assignments.map(assignment => (
                <div key={assignment.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-medium text-gray-900">
                          {assignment.template?.name || assignment.templateName}
                        </h4>
                        <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                          {assignment.tenantName}
                        </span>
                        {assignment.status === 'active' ? (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle size={12} />
                            Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <XCircle size={12} />
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Users size={14} />
                          {assignment.config?.approvers?.reduce((sum, stage) => 
                            sum + (stage.approvers?.length || 0), 0
                          ) || 0} approvers
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          {assignment.config?.timeoutHours || 48}h timeout
                        </div>
                        <div className="flex items-center gap-1">
                          <AlertCircle size={14} />
                          Escalation: {assignment.config?.escalationEnabled ? 'On' : 'Off'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleEditAssignment(assignment)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                        title="Edit Assignment"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => removeAssignment(assignment.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Assignment Configuration Modal */}
      {showAssignModal && selectedTemplate && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">
                    {editingAssignment ? 'Edit' : 'Configure'} Workflow Assignment
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {editingAssignment ? 'Updating' : 'Assigning'} "{selectedTemplate.name}" to {selectedTenant.name}
                  </p>
                </div>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Approvers Configuration */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Configure Approvers</h4>
                
                {/* Add Approver Form */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Stage
                      </label>
                      <select
                        value={newApprover.stage}
                        onChange={(e) => setNewApprover(prev => ({ ...prev, stage: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="">Select stage...</option>
                        {selectedTemplate && selectedTemplate.stages ? selectedTemplate.stages
                          .filter(s => {
                            // Include all stage types that can have approvers
                            // Exclude only 'notify' stages as they don't need approvers
                            return s.type !== 'notify';
                          })
                          .map(stage => (
                            <option key={stage.id} value={stage.id}>
                              {stage.name || `Stage ${stage.id.replace('stage_', '')}`}
                            </option>
                          )) : (
                            <option disabled>No stages available</option>
                          )}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={newApprover.name}
                        onChange={(e) => setNewApprover(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="John Doe"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={newApprover.email}
                        onChange={(e) => setNewApprover(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="approver@example.com"
                      />
                    </div>
                    
                    <div className="flex items-end">
                      <button
                        onClick={addApprover}
                        className="w-full px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                      >
                        <Plus size={14} className="inline mr-1" />
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Current Approvers */}
                <div className="space-y-2">
                  {(assignmentConfig.approvers || []).map(stage => (
                    <div key={stage.stageId} className="border border-gray-200 rounded-lg p-3">
                      <h5 className="font-medium text-sm text-gray-900 mb-2">
                        {stage.stageName}
                      </h5>
                      {!stage.approvers || stage.approvers.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No approvers assigned</p>
                      ) : (
                        <div className="space-y-1">
                          {(stage.approvers || []).map(approver => (
                            <div key={approver.id} className="flex items-center justify-between py-1">
                              <div className="flex items-center gap-2">
                                <User size={14} className="text-gray-400" />
                                <span className="text-sm">
                                  {approver.name} ({approver.email})
                                </span>
                              </div>
                              <button
                                onClick={() => removeApprover(stage.stageId, approver.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeout Configuration */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Timeout & Escalation</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timeout (hours)
                    </label>
                    <input
                      type="number"
                      value={assignmentConfig.timeoutHours}
                      onChange={(e) => setAssignmentConfig(prev => ({ 
                        ...prev, 
                        timeoutHours: parseInt(e.target.value) 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      min="1"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={assignmentConfig.escalationEnabled}
                        onChange={(e) => setAssignmentConfig(prev => ({ 
                          ...prev, 
                          escalationEnabled: e.target.checked 
                        }))}
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Enable escalation on timeout
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Notification Emails */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Additional Notification Emails</h4>
                <textarea
                  value={(assignmentConfig.notificationEmails || []).join('\n')}
                  onChange={(e) => setAssignmentConfig(prev => ({
                    ...prev,
                    notificationEmails: e.target.value.split('\n').filter(email => email.trim())
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                  placeholder="Enter email addresses, one per line..."
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveAssignment}
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save size={16} />
                {editingAssignment ? 'Update' : 'Save'} Assignment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowAssignment;